import { canShareFortuneImage, sharePreparedFortuneImage } from './fortune-image-file';
export { imageFileDataUrl, canShareFortuneImage, sharePreparedFortuneImage } from './fortune-image-file';
import { fortuneSavePolicy, type FortuneSavePolicy } from './fortune-save-policy';
import { showFortuneImagePreview } from './fortune-save-dialog';

export type FortuneSaveMode = 'download' | 'preview' | 'shared' | 'cancelled';

export interface FortuneSavePlatform {
  readonly userAgent: string;
  readonly maxTouchPoints?: number;
  readonly canShare: (file: File) => boolean;
  readonly share: (file: File) => Promise<'shared' | 'cancelled' | 'unavailable'>;
  readonly preview: (file: File, policy: FortuneSavePolicy, shareFailed: boolean) => void;
  readonly download: (file: File) => void;
}

export function needsFortuneImagePreview(userAgent: string, maxTouchPoints = 0): boolean {
  return fortuneSavePolicy(userAgent, maxTouchPoints).preview;
}

function downloadImage(file: File): void {
  const url = URL.createObjectURL(file);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = file.name;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Call with a pre-rendered File directly from a click/tap handler. */
export async function saveFortuneImage(
  file: File,
  returnFocusTo?: HTMLElement,
  platform: FortuneSavePlatform = {
    userAgent: navigator.userAgent,
    maxTouchPoints: navigator.maxTouchPoints,
    canShare: canShareFortuneImage,
    share: sharePreparedFortuneImage,
    preview: (image, policy, failed) => showFortuneImagePreview(image, policy, failed, returnFocusTo),
    download: downloadImage,
  },
): Promise<FortuneSaveMode> {
  const policy = fortuneSavePolicy(platform.userAgent, platform.maxTouchPoints);
  if (policy.preferNativeShare && platform.canShare(file)) {
    const outcome = await platform.share(file);
    if (outcome !== 'unavailable') return outcome;
    // Sharing consumes activation. Alternatives need a NEW tap; never click
    // a fallback download or open a window automatically after a rejection.
    platform.preview(file, policy, true);
    return 'preview';
  }
  if (policy.preview) {
    platform.preview(file, policy, false);
    return 'preview';
  }
  platform.download(file);
  return 'download';
}
